import { useEffect, useState } from 'react';
import { Send } from 'lucide-react';
import { isStorageKind } from './destination-kinds';
import type { PaigeDestination, PaigeDestinationsBridge } from './paige.global';

/**
 * Under a destination's row: "Send the 12 documents already exported", for a destination added after
 * the job exported them, which otherwise receives only what is confirmed from then on. Sends them to
 * this destination alone (POST .../backfill); the others already have them. Nothing while the
 * destination is missing none, or cannot deliver yet (a storage one not connected or with no folder).
 */
export function BackfillOffer({ row }: { row: PaigeDestination }) {
  const bridge = window.paigeWorkbench?.destinations;
  const ready = canDeliver(row);
  const [missing, setMissing] = useState<number | null>(null);
  const [line, setLine] = useState<string | null>(null);
  useEffect(() => {
    if (!bridge?.backfillCount || !ready) return undefined;
    let live = true;
    bridge
      .backfillCount(row.id)
      .then((count) => live && setMissing(count))
      .catch(() => live && setMissing(null));
    return () => {
      live = false;
    };
  }, [bridge, row.id, ready]);
  if (line) return <small className="paige-destination-backfill">{line}</small>;
  if (!bridge?.backfill || !ready || !missing) return null;
  const send = async () => {
    setLine(`Sending to ${row.label}...`);
    try {
      setLine(sentLine(await (bridge as Required<PaigeDestinationsBridge>).backfill(row.id), row));
      setMissing(0);
    } catch (failure) {
      setLine(failure instanceof Error ? failure.message : 'Paige could not queue them');
    }
  };
  return (
    <button
      type="button"
      className="quiet-button small paige-destination-backfill"
      onClick={() => void send()}
    >
      <Send size={12} aria-hidden="true" />
      {offerLine(missing)}
    </button>
  );
}

/** The button's words for the count. */
export function offerLine(count: number): string {
  return count === 1
    ? 'Send the 1 document already exported'
    : `Send the ${count} documents already exported`;
}

/** What the row says once they are queued. */
export function sentLine(count: number, row: Pick<PaigeDestination, 'label'>): string {
  if (count === 0) return `${row.label} already has every exported document`;
  return `${count === 1 ? '1 document' : `${count} documents`} on the way to ${row.label}`;
}

/** Download is every document already; a storage destination needs its account and a folder first. */
export function canDeliver(row: PaigeDestination): boolean {
  if (row.kind === 'download') return false;
  if (!isStorageKind(row.kind)) return true;
  const { connection, location } = row.settings;
  return Boolean(connection && connection.state !== 'reconnect' && location);
}
